import { useQuery } from "@tanstack/react-query";
import { unauthedV3Request } from "utils/api/requests";

// How often to re-poll while the batch is still being zipped. The processing
// card promises the page auto-refreshes the moment it's ready, so we poll until
// `status` leaves "processing" (or the request errors), then stop.
const PROCESSING_POLL_MS = 15000;

/**
 * Public download-batch metadata for the landing page. The token is the sole
 * capability (no auth); the endpoint returns NO presigned URLs — only the
 * status + per-part readiness the UI renders. Errors carry an HTTP `statusCode`
 * (from utils/request's parseJSON): 410 → expired, 404 → not-found, else error.
 *
 * @param {string} token - the raw download token from the emailed landing URL
 */
export const useGetDownloadBatch = (token) =>
  useQuery({
    queryKey: ["download-batch", token],
    queryFn: () =>
      unauthedV3Request(`v3/downloads/${encodeURIComponent(token)}`, {
        method: "GET",
      }),
    enabled: !!token,
    // Client errors (404 expired-or-unknown, 410 expired) are terminal — the
    // token won't become valid on retry; only retry transient 5xx/network.
    retry: (failureCount, error) => {
      if (error?.statusCode && error.statusCode >= 400 && error.statusCode < 500) return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // react-query v4 passes the current data as the first arg. Poll only while
    // processing; any other status (or an error, where data is undefined) stops.
    refetchInterval: (data) => (data?.status === "processing" ? PROCESSING_POLL_MS : false),
  });
